import { Formik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import * as yup from "yup";
import { Button } from "../../../components/ui/button";
import Card from "../../../components/ui/card-snippet";
import { useLocation, useNavigate } from "react-router-dom";
import {
    respEnum,
    GenderTypeEnum,
} from "../../../constants/appConstant";
import {
    FormikInput,
    FormikSwitch,
    FormikSearchSelect,
    FormikSelect,
} from "../../../components/formik-components";
import Common from "../../../helper/common";
import { useQueryParams } from "../../../contexts/queryParamsContext";

const formValidations = yup.object({
    firstName: yup.string().required("First Name is required"),
    lastName: yup.string().required("Last Name is required"),
    email: yup
        .string()
        .email("Invalid email format")
        .required("Email is required"),
    roleId: yup.string().required("Role is required"),
    gender: yup.string().required("Gender is required"),
    password: yup.string().when("id", (id, schema) => {
        return id == 0
            ? schema
                .required("Password is required")
                .min(6, "Password must be at least 6 characters")
            : schema.notRequired();
    }),
});

export default function AddOtherUser() {
    const navigate = useNavigate();
    const formikRef = useRef();
    const [clubs, setClubs] = useState([]);
    const [tournaments, setTournaments] = useState([]);
    const location = useLocation();
    const { params, setParams } = useQueryParams();
    const id = params.userId ? parseInt(params.userId) : 0;
    const [roleList, setRoleList] = useState([]);

    useEffect(() => {
        // getClubs();
        getGlobalRoles();
        getTournaments();
        if (id) {
            getUserDetail();
        }

        return () => {
            setParams({
                ...params,
                userId: null,
            });
        };
    }, [id]);

    const formInitialValue = {
        id: 0,
        firstName: "",
        lastName: "",
        email: "",
        gender: GenderTypeEnum.Male.toString(),
        roleId: null,
        isactive: true,
        isVerified: true,
        clubIds: [],
        tournamentIds: [],
        password: "",
    };

    /*
    const getClubs = async () => {
      const response = await Common.ApiService.getInstance().request(
        "GetClubsDDL"
      );
      if (response?.status == respEnum.Success && response?.data) {
        setClubs(response.data);
      }
    };
    */

    const getTournaments = async () => {
        const response = await Common.ApiService.getInstance().request(
            "GetTournamentDDL"
        );
        if (response?.status == respEnum.Success && response?.data) {
            setTournaments(response.data);
        }
    };

    const getUserDetail = async () => {
        let response = await Common.ApiService.getInstance().request(
            `GetUserDetails?userId=${id}`
        );

        if (response.status == respEnum.Success && response?.data?.length > 0) {
            let userData = response.data[0];
            if (formikRef.current) {
                formikRef.current.setValues({
                    ...userData,
                    password: "", // Clear password for security
                    // Convert IDs to string format for select components
                    roleId: userData.roleId?.toString(),
                    gender: userData.gender?.toString(),
                });
            }
        }
    };

    const getGlobalRoles = async () => {
        try {
            const response = await Common.ApiService.getInstance().request(
                `GetGlobalRoles`,
                "GET"
            );

            const data = [
                { id: null, name: "Select Role" },
                ...(response?.data ?? []).map(r => ({
                    ...r,
                    id: r.id?.toString()   // 🔑 convert to string
                }))
            ];

            setRoleList(data);
            return data;
        } catch (err) {
            console.error("getRolesByClub error:", err);
            throw err;
        }
    };

    const onSubmitData = async (data, action) => {
        const formData = {
            ...data,
            id: id || 0,
            roleId: parseInt(data.roleId),
            gender: parseInt(data.gender),
        };

        // Validate additional field requirements based on role
        /*
        if (
          formData.roleId === RoleEnum.Club_Manager &&
          (!formData.clubIds || formData.clubIds.length === 0)
        ) {
          Common.showToast("Please select at least one club", "Error", "error");
          return;
        }
        */

        const selectedRole = roleList.find(r => r.id === formData.roleId);
        // Only validate if Tournament Manager
        if (
            selectedRole?.name === "Tournament Manager" &&
            (!formData.tournamentIds || formData.tournamentIds.length === 0)
        ) {
            Common.showToast(
                "Please select at least one tournament",
                "Error",
                "error"
            );
            return;
        }

        let response = await Common.ApiService.getInstance().request(
            "AddUpdateUser",
            formData,
            "POST"
        );
        action.setSubmitting(false);

        if (response.status == respEnum.Success) {
            Common.SweetAlert.alert(response.message);
            setTimeout(() => {
                navigate("/app/users");
            }, 1000);
        } else {
            //Common.showToast(response.message, "Error", "error");
        }
    };

    const handleRoleChange = (value, formikProps) => {
        formikProps.setFieldValue("roleId", value || ""); // always string
    
        const selectedRole = roleList.find(r => r.id === value);
    
        if (selectedRole?.name === "Tournament Manager") {
            formikProps.setFieldValue("clubIds", []);
            formikProps.setFieldValue("isVerified", true);
        } else {
            // reset if "Select Role" or any other role
            formikProps.setFieldValue("clubIds", []);
            formikProps.setFieldValue("tournamentIds", []);
        }
    };    

    const genderOptions = [
        { id: null, name: "Select Gender" },
        { id: GenderTypeEnum.Male.toString(), name: "Male" },
        { id: GenderTypeEnum.Female.toString(), name: "Female" },
        { id: GenderTypeEnum.Other.toString(), name: "Other" },
    ];

    return (
        <div className="card-holder">
            <Card title={id ? "Update User" : "Add User"}>
                <div className="p-5">
                    <Formik
                        innerRef={formikRef}
                        validationSchema={formValidations}
                        initialValues={formInitialValue}
                        onSubmit={onSubmitData}
                        validateOnChange={false}
                        validateOnBlur={false}
                        enableReinitialize
                    >
                        {(formikProps) => (
                            <form onSubmit={formikProps.handleSubmit}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ">
                                    <FormikInput
                                        label="First Name"
                                        name="firstName"
                                        placeholder="First Name"
                                        formikProps={formikProps}
                                    />
                                    <FormikInput
                                        label="Last Name"
                                        name="lastName"
                                        placeholder="Last Name"
                                        formikProps={formikProps}
                                    />
                                    <FormikInput
                                        label="Email"
                                        name="email"
                                        placeholder="Email Address"
                                        formikProps={formikProps}
                                        type="email"
                                    />
                                    <FormikSelect
                                        label="Gender"
                                        name="gender"
                                        data={genderOptions}
                                        formikProps={formikProps}
                                    />
                                    <FormikSelect
                                        label="Role"
                                        name="roleId"
                                        data={roleList}
                                        formikProps={formikProps}
                                        onChange={(value) => handleRoleChange(value, formikProps)}
                                    />
                                    <FormikInput
                                        label="Password"
                                        name="password"
                                        placeholder={
                                            id ? "Leave blank to keep current password" : "Password"
                                        }
                                        formikProps={formikProps}
                                        type="password"
                                    />
                                    <FormikSwitch
                                        label="Active"
                                        name="isactive"
                                        formikProps={formikProps}
                                    />
                                    <FormikSwitch
                                        label="Verified"
                                        name="isVerified"
                                        formikProps={formikProps}
                                    />

                                    {/*
                                     {parseInt(formikProps.values.roleId) ===
                                       RoleEnum.Club_Manager && (
                                       <div className="col-span-2">
                                         <FormikSearchSelect
                                           label="Clubs"
                                           name="clubIds"
                                           placeholder="Select Clubs"
                                           data={clubs}
                                           formikProps={formikProps}
                                           isMulti
                                         />
                                       </div>
                                     )}
                                       */}

                                    {(() => {
                                        const selectedRole = roleList.find(r => r.id === formikProps.values.roleId);
                                        return selectedRole?.name === "Tournament Manager" ? (
                                            <div className="col-span-2">
                                                <FormikSearchSelect
                                                    label="Tournaments"
                                                    name="tournamentIds"
                                                    placeholder="Select Tournaments"
                                                    data={tournaments}
                                                    formikProps={formikProps}
                                                    isMulti
                                                />
                                            </div>
                                        ) : null;
                                    })()}

                                </div>

                                <div className="flex justify-end mt-6 space-x-3">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => navigate("/app/users")}
                                    >
                                        Cancel
                                    </Button>
                                    <Button type="submit" disabled={formikProps.isSubmitting}>
                                        {formikProps.isSubmitting
                                            ? "Saving..."
                                            : id
                                                ? "Update"
                                                : "Save"}
                                    </Button>
                                </div>
                            </form>
                        )}
                    </Formik>
                </div>
            </Card>
        </div>
    );
}
